import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatError } from "@/services/api";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound } from "lucide-react";

export default function ChangePasswordSettingsPage() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-lg" data-testid="change-password-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-[#003DA5]/10 flex items-center justify-center">
          <KeyRound className="w-4 h-4 text-[#003DA5]" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#09090B]" data-testid="change-password-settings-title">
            Change Password
          </h1>
          <p className="text-sm text-[#52525B] mt-0.5">Update your account password</p>
        </div>
      </div>

      <Card className="border-[#E4E4E7] shadow-none rounded-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="change-password-settings-form">
          <div className="space-y-2">
            <Label htmlFor="currentPassword" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
              Current Password
            </Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrent ? "text" : "password"}
                data-testid="settings-current-password-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="rounded-sm border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5] pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
              New Password
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNew ? "text" : "password"}
                data-testid="settings-new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="rounded-sm border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5] pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
              Confirm New Password
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              data-testid="settings-confirm-password-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              className="rounded-sm border-zinc-300 h-10 focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5]"
              required
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              data-testid="settings-change-password-submit"
              className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white font-semibold h-10 text-sm px-6"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="rounded-sm h-10 text-sm px-6"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
